import { z } from 'zod';
import {
  evaluateScoreExpression,
  expressionLimits,
  parseScoreExpression,
  type ScoreMetrics,
} from './expression';
import { SCORING_METRICS_VERSION } from './versions';

export const ScoringRuleSchema = z
  .object({
    version: z.literal(1),
    metricsVersion: z.union([
      z.literal(1),
      z.literal(SCORING_METRICS_VERSION),
      z.literal(3),
    ]),
    direction: z.enum(['minimize', 'maximize']),
    formula: z.string().min(1).max(expressionLimits.characters),
  })
  .strict()
  .superRefine((rule, context) => {
    try {
      parseScoreExpression(rule.formula);
    } catch (error) {
      context.addIssue({
        code: 'custom',
        path: ['formula'],
        message: error instanceof Error ? error.message : 'Invalid scoring formula.',
      });
    }
  });
export type ScoringRule = z.infer<typeof ScoringRuleSchema>;
/** Recover unfinished text without admitting it at publish/import/run boundaries. */
export const DraftScoringRuleSchema = z
  .object({
    ...ScoringRuleSchema.shape,
    formula: z.string().max(expressionLimits.characters),
  })
  .strict();
const metricValue = z.number().int().min(0).max(expressionLimits.value);
export const ScoreMetricsSchema = z
  .object({
    wires: metricValue,
    notGates: metricValue,
    diodes: metricValue,
    totalTiles: metricValue,
    area: metricValue,
  })
  .strict();
/** History stores an opaque canonical identity, never an executable AST. */
export const ObjectiveScoreSchema = z
  .object({
    version: z.literal(1),
    metricsVersion: z.union([
      z.literal(1),
      z.literal(SCORING_METRICS_VERSION),
      z.literal(3),
    ]),
    key: z.string().min(1).max(4096),
    value: z.number().min(-expressionLimits.value).max(expressionLimits.value),
    metrics: ScoreMetricsSchema,
  })
  .strict();
export type ObjectiveScore = z.infer<typeof ObjectiveScoreSchema>;
export const scoringPresets = [
  { id: 'wires', label: 'Fewest wires', formula: 'wires' },
  { id: 'not-gates', label: 'Fewest NOT gates', formula: 'notGates' },
  { id: 'diodes', label: 'Fewest diodes', formula: 'diodes' },
  { id: 'tiles', label: 'Fewest total tiles', formula: 'totalTiles' },
  { id: 'area', label: 'Smallest footprint', formula: 'area' },
] as const;
export type ScoringPreset = (typeof scoringPresets)[number]['id'];
export function presetScoring(id: ScoringPreset, metricsVersion: 1 | 2 = 1): ScoringRule {
  const preset = scoringPresets.find((entry) => entry.id === id);
  if (!preset) throw new Error('Unknown scoring preset.');
  return {
    version: 1,
    metricsVersion,
    direction: 'minimize',
    formula: preset.formula,
  };
}
export function scoringRuleKey(rule: ScoringRule): string {
  const valid = ScoringRuleSchema.parse(rule);
  return JSON.stringify([
    valid.version,
    valid.metricsVersion,
    valid.direction,
    parseScoreExpression(valid.formula),
  ]);
}
export function evaluateScoring(rule: ScoringRule, input: ScoreMetrics) {
  const valid = ScoringRuleSchema.parse(rule);
  const metrics = ScoreMetricsSchema.parse(input);
  const evaluation = evaluateScoreExpression(valid.formula, metrics);
  const snapshot: ObjectiveScore = {
    version: 1,
    metricsVersion: valid.metricsVersion,
    key: scoringRuleKey(valid),
    value: evaluation.value,
    metrics,
  };
  return { snapshot, breakdown: evaluation.breakdown, usedMetrics: evaluation.metrics };
}
export function compatibleScore(score: ObjectiveScore, rule: ScoringRule) {
  return (
    score.version === rule.version &&
    score.metricsVersion === rule.metricsVersion &&
    score.key === scoringRuleKey(rule)
  );
}
/** Recompute numeric history from bounded metrics; opaque keys are never executed. */
export function verifiedObjectiveScore(score: ObjectiveScore, rule: ScoringRule) {
  const parsed = ObjectiveScoreSchema.safeParse(score);
  if (!parsed.success || !compatibleScore(parsed.data, rule)) return false;
  try {
    return (
      evaluateScoring(rule, parsed.data.metrics).snapshot.value === parsed.data.value
    );
  } catch {
    return false;
  }
}
/** Exact scalar ties remain ties: there is no hidden wire/area tiebreaker. */
export function compareObjectiveScores(
  next: ObjectiveScore,
  previous: ObjectiveScore,
  rule: ScoringRule,
): 'better' | 'tied' | 'worse' | 'incomparable' {
  if (!compatibleScore(next, rule) || !compatibleScore(previous, rule))
    return 'incomparable';
  if (next.value === previous.value) return 'tied';
  return (
    rule.direction === 'minimize'
      ? next.value < previous.value
      : next.value > previous.value
  )
    ? 'better'
    : 'worse';
}
